import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useLeaderboard } from '../hooks/useLeaderboard';
import { useLeaderboardScopes } from '../hooks/useLeaderboardScopes';
import LeaderboardBoard from '../components/LeaderboardBoard';
import { slugify } from '../../infrastructure/utils/slug';
import LocalizationService from '../../application/services/LocalizationService';
import './LeaderboardPages.css';

const t = (key: string, options?: Record<string, unknown>) =>
  LocalizationService.translate(`leaderboard.${key}`, undefined, options) as string;

/** /leaderboard/:countryCode/:citySlug — in-city ranking. */
const CityLeaderboardPage: React.FC = () => {
  const { countryCode = '', citySlug = '' } = useParams<{ countryCode: string; citySlug: string }>();
  const [page, setPage] = useState(0);
  const { scopes, isLoading: scopesLoading } = useLeaderboardScopes();

  const country = useMemo(
    () => scopes?.countries.find((c) => c.code.toLowerCase() === countryCode.toLowerCase()),
    [scopes, countryCode]
  );
  const city = useMemo(
    () =>
      country &&
      scopes?.cities.find((c) => c.country_id === country.id && slugify(c.name) === citySlug.toLowerCase()),
    [scopes, country, citySlug]
  );

  const { board, isLoading, error } = useLeaderboard('CITY', city?.id ?? null, page, !!city);

  if (!scopesLoading && !city) {
    return (
      <div className="lbp-root">
        <div className="lbp-not-found">
          <h1>{t('notFoundCityTitle')}</h1>
          <p>
            <Link to={country ? `/leaderboard/${countryCode.toLowerCase()}` : '/leaderboard'}>
              {country ? t('backTo', { name: country.name }) : t('backToGlobal')}
            </Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="lbp-root">
      <nav className="lbp-breadcrumb">
        <Link to="/leaderboard">{t('globalLabel')}</Link>
        <span>/</span>
        <Link to={`/leaderboard/${countryCode.toLowerCase()}`}>{country?.name || countryCode}</Link>
        <span>/</span>
        <span>{city?.name || citySlug}</span>
      </nav>

      <header className="lbp-header">
        <span className="lbp-eyebrow">{t('eyebrow')}</span>
        <h1 className="lbp-headline">{city?.name || citySlug} {t('countryHeadlineSuffix')}</h1>
        <p className="lbp-sub">{t('citySub')}</p>
      </header>

      {(isLoading || scopesLoading) && <div className="lbp-loading">{t('loading')}</div>}
      {error && <div className="lbp-error">{error}</div>}
      {board && <LeaderboardBoard board={board} page={page} onPageChange={setPage} />}
    </div>
  );
};

export default CityLeaderboardPage;
